/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

function getTimeParts(
  timeInSeconds: number,
  forceIncludeHours: boolean = false
): number[] {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds - hours * 3600) / 60);
  const seconds = Math.floor((timeInSeconds - hours * 3600) % 60);

  if (hours || forceIncludeHours) {
    return [hours, minutes, seconds];
  } else {
    return [minutes, seconds];
  }
}

export function formatSeconds(
  timeInSeconds: number,
  forceIncludeHours = false
): string {
  return getTimeParts(timeInSeconds, forceIncludeHours)
    .map((value: number, index: number): string =>
      index != 0 && value < 10 ? `0${value}` : String(value)
    )
    .join(":");
}

export function formatSecondsMatching(
  timeInSeconds: number,
  matchingTimeInSeconds: number
): string {
  return formatSeconds(timeInSeconds, matchingTimeInSeconds >= 3600);
}

/**
 * Formats seconds into a human-readable string with 'h', 'm', and 's' units.
 * For example: "5m 23s" or "1h 5m 23s"
 * Uses i18n for localization of time units.
 *
 * @param timeInSeconds The time in seconds to format
 * @param includeZeroValues Whether to include parts that are zero (e.g., "0h 5m 23s" vs "5m 23s")
 * @returns Formatted time string
 */
export function formatSecondsHumanReadable(
  timeInSeconds: number,
  includeZeroValues = false
): string {
  const parts = getTimeParts(timeInSeconds, true);
  const [hours, minutes, seconds] = parts;

  let result = "";

  if (hours > 0 || includeZeroValues) {
    result +=
      I18n.t("time.formats.human_readable.hours", { count: hours }) + " ";
  }

  if (minutes > 0 || includeZeroValues || hours > 0) {
    result +=
      I18n.t("time.formats.human_readable.minutes", { count: minutes }) + " ";
  }

  result += I18n.t("time.formats.human_readable.seconds", { count: seconds });

  return result;
}

export function millisecondsToSeconds(milliseconds: number, fixed = 3): number {
  return parseFloat((milliseconds / 1000.0).toFixed(fixed));
}

export function secondsToMilliseconds(seconds: number): number {
  return Math.round(seconds * 1000);
}

export function msToFullMinutes(ms: number): number {
  return Math.floor(ms / 1000 / 60);
}
